import { useContext } from 'react';
import { useQuery } from 'react-query';
import { ServerErrorResponse } from 'data_sources/constants';
import { RiskPoolOrganization } from '../../_types/risk_pool';
import { generateRiskPoolMetrics } from './riskPoolMetricsGenerator';
import { getRiskPoolOrganizations } from './useRiskPoolOrganizations';
import { useRiskPoolDemoData } from './useRiskPoolDemoData';
import { AppContext } from 'views/App/context';
import { RiskPoolOrgContext } from '../RiskPoolOrganizations/Detail/RiskPoolOrgContext';
import _ from 'lodash';

interface RiskPoolOrganizationMetricsParams {
  startDate?: string;
  endDate?: string;
  search?: string;
  trend?: string;
  compliance?: string;
  policyMods?: string;
  [key: string]: any;
}

// Helper function to get fallback dates: previous year beginning of year, previous month end of month
const getFallbackDates = (): { startDate: string; endDate: string } => {
  const today = new Date();
  const previousYear = today.getFullYear() - 1;
  const previousMonth = today.getMonth() - 1;
  
  // Start date: beginning of previous year (January 1st)
  const startDate = new Date(previousYear, 0, 1);
  
  // End date: end of previous month
  const endDate = new Date(today.getFullYear(), previousMonth + 1, 0);
  
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

export default function useRiskPoolOrganizationMetrics(
  childOrgId: string,
  params: RiskPoolOrganizationMetricsParams = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const { currentOrganizationId } = useContext(AppContext);
  const parentOrgId = currentOrganizationId || childOrgId; // Fallback to childOrgId if not available
  const isDemoDataEnabled = useRiskPoolDemoData();

  // Try to get dates from RiskPoolOrgContext if available
  const riskPoolOrgContext = useContext(RiskPoolOrgContext);
  const contextDates = riskPoolOrgContext?.queryParams;

  // Use context dates if available, otherwise use params, otherwise use fallback
  const fallbackDates = getFallbackDates();
  const finalParams = {
    ...params,
    startDate: params.startDate || contextDates?.startDate || fallbackDates.startDate,
    endDate: params.endDate || contextDates?.endDate || fallbackDates.endDate,
  };

  return useQuery<RiskPoolOrganization, Error>(
    ['risk_pool_organization_metrics', parentOrgId, childOrgId, finalParams],
    async ({ signal }): Promise<RiskPoolOrganization> => {
      try {
        if (isDemoDataEnabled) {
          // Get the filtered data using the same logic as useRiskPoolMetrics
          const data = generateRiskPoolMetrics(childOrgId, finalParams);

          // Find the specific organization by ID
          const organization = _.find(data, (org) => org.id === childOrgId);

          if (!organization) {
            throw new Error(`Organization with ID ${childOrgId} not found`);
          }

          return organization;
        }

        // Fetch from API: get all organizations and find the matching one
        const response = await getRiskPoolOrganizations(parentOrgId, finalParams, signal, onError);
        const organization = _.find(response.organizations, (org) => org.id === childOrgId);

        if (!organization) {
          throw new Error(`Organization with ID ${childOrgId} not found`);
        }

        return organization;
      } catch (err) {
        if (onError) onError(err as ServerErrorResponse);
        throw err;
      }
    },
    {
      ...options,
      enabled: !!childOrgId && !!parentOrgId,
    }
  );
}
